import Pagination from "../../components/ui/Pagination";
import StatusAction from "../../components/ui/StatusAction";
import { getCustomerDisplayName } from "../../utils/customerDisplay";
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Reuse the Products page styles (tables, dropdowns, pagination, etc.)
import "../ProductsPage/ProductsPage.css";

import { getCarts, updateCartStatus } from "../../services/cartService";
import { Order } from "../../types/order";
import PageLoader from "../../compoonents/PageLoader";
import {
  PageFilterDropdown,
  PageSearchBar,
  PageToolbar,
} from "../../compoonents/PageToolbar";

import { FiEye } from "react-icons/fi";


// Cart status uses the shop-for-me enum (EShopForMeStatusType — lowercase values)
const CART_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "request", label: "Request" },
  { value: "successful", label: "Successful" },
  { value: "failed", label: "Failed" },
  { value: "payment_confirmed", label: "Payment Confirmed" },
  { value: "payment_cancelled", label: "Payment Cancelled" },
  { value: "refund_processing", label: "Refund Processing" },
  { value: "refund_confirmed", label: "Refund Confirmed" },
  { value: "shopping_ongoing", label: "Shopping Ongoing" },
  { value: "product_checked_in", label: "Product Checked In" },
  { value: "arrived_at_hub", label: "Arrived At Hub" },
  { value: "delivered", label: "Delivered" },
  { value: "handled_by_user", label: "Handled By User" },
];

const formatCartStatus = (status: string): string => {
  const match = CART_STATUS_OPTIONS.find(
    (o) => o.value.toLowerCase() === (status || "").toLowerCase(),
  );
  return match ? match.label : (status || "N/A").replace(/_/g, " ");
};

const getStatusColor = (status: string): string => {
  switch ((status || "").toLowerCase()) {
    case "request":
    case "product_checked_in":
      return "bg-blue-100 text-blue-800";
    case "successful":
    case "payment_confirmed":
    case "refund_confirmed":
    case "delivered":
      return "bg-green-100 text-green-800";
    case "failed":
    case "payment_cancelled":
      return "bg-red-100 text-red-800";
    case "refund_processing":
      return "bg-yellow-100 text-yellow-800";
    case "shopping_ongoing":
      return "bg-purple-100 text-purple-800";
    case "arrived_at_hub":
      return "bg-indigo-100 text-indigo-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const CartStatusDropdown: React.FC<{ currentStatus: string; onStatusUpdate: (newStatus: string) => Promise<void>; }> = ({ currentStatus, onStatusUpdate }) => (
  <StatusAction value={currentStatus} label={formatCartStatus(currentStatus)} badgeClassName={getStatusColor(currentStatus)} options={CART_STATUS_OPTIONS}
    onChange={async status => {
      try { await onStatusUpdate(status); toast.success("Status updated"); }
      catch { toast.error("Failed to update status"); }
    }} />
);

const AmazonPage: React.FC = () => {
  const navigate = useNavigate();

  const [carts, setCarts] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

  useEffect(() => {
    const fetchCarts = async () => {
      setLoading(true);
      try {
        const response = await getCarts(
          currentPage,
          10,
          searchTerm,
          filterStatus,
        );
        setCarts(response.data?.allOrders || []);
        setTotalPages(response.data?.pagination?.totalPages ?? 1);
      } catch (err) {
        console.error("Failed to fetch amazon carts:", err);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(() => fetchCarts(), 400);
    return () => clearTimeout(timer);
  }, [currentPage, searchTerm, filterStatus]);

  const visibleCarts = useMemo(() => {
    return carts.filter((c) => {
      const searchLower = searchTerm.toLowerCase();
      const name = getCustomerDisplayName(c.user).toLowerCase();
      const email = (c.user?.email ?? "").toLowerCase();
      const matchSearch =
        name.includes(searchLower) ||
        email.includes(searchLower) ||
        c.id.toLowerCase().includes(searchLower);

      const matchStatus = filterStatus
        ? (c.status || "").toLowerCase() === filterStatus.toLowerCase()
        : true;
      return matchSearch && matchStatus;
    });
  }, [carts, searchTerm, filterStatus]);

  return (
    <div className="products-page">

      <header className="page-header">
        <h3>Amazon</h3>
        <div className="page-controls">
          <PageToolbar>
            <PageFilterDropdown
              value={filterStatus}
              options={CART_STATUS_OPTIONS}
              onChange={setFilterStatus}
            />
            <PageSearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search orders..."
            />
          </PageToolbar>
        </div>
      </header>

      <div className="page-content-wrapper">
        <div className="product-content">
          {loading ? (
            <PageLoader message="Loading orders..." />
          ) : visibleCarts.length === 0 ? (
            <div
              style={{ textAlign: "center", padding: "40px", color: "#666" }}
            >
              No orders found.
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer Name</th>
                    <th>Email</th>
                    <th>Date Created</th>
                    <th>Items</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleCarts.map((cart) => (
                    <tr key={cart.id}>
                      <td data-label="Order ID">
                        {cart.id.substring(0, 8)}...
                      </td>
                      <td data-label="Customer Name">
                        {getCustomerDisplayName(cart.user)}
                      </td>
                      <td data-label="Email">{cart.user?.email}</td>
                      <td data-label="Date Created">
                        {new Date(cart.createdAt).toLocaleDateString()}
                      </td>
                      <td data-label="Items">{cart.items?.length ?? 0}</td>
                      <td data-label="Status">
                        <CartStatusDropdown
                          currentStatus={cart?.status}
                          onStatusUpdate={async (newStatus) => {
                            await updateCartStatus(cart.id, newStatus);
                            setCarts((curr) =>
                              curr.map((c) =>
                                c.id === cart.id
                                  ? { ...c, status: newStatus }
                                  : c,
                              ),
                            );
                          }}
                        />
                      </td>
                      <td data-label="Action">
                        <div className="table-action-buttons">
                          <button
                            type="button"
                            className="table-action-btn"
                            title="View order"
                            aria-label="View order"
                            onClick={() => navigate(`/amazon/${cart.id}`)}
                          >
                            <FiEye />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && visibleCarts.length > 0 && (
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} loading={loading} />
        )}
      </div>
    </div>
  );
};

export default AmazonPage;
